import dayjs from 'dayjs'
import type { Manager } from '@/api/types/crm/crm.types'
import type {
  CrmLeadTask,
  CrmLeadTaskSticker,
  CrmLeadTasksStatus,
} from '@/api/types/crm/crmLeadTasks.types'
import type {
  CreateTaskTrackerTaskFieldValue,
  UpdateTaskTrackerTaskRequest,
} from '@pages/Admin/TaskTrackerCRM/store/taskTracker.types'
import { isTaskTrackerSystemDatetimeStickerFieldType } from '@pages/Admin/TaskTrackerCRM/lib/taskTrackerBoard.stickerFilters.utils'
import { normalizeTaskDeadline } from '@pages/Admin/TaskTrackerCRM/lib/taskTrackerTaskInfo.utils'
import { normalizeTaskPerformerIds } from '@pages/Admin/TaskTrackerCRM/lib/taskTrackerPerformers.utils'

export const CRM_LEAD_TASKS_SYSTEM_KEY = 'crm_lead' as const

/** Дефолт per-page для вкладки «Задачи» (как в истории). */
export const CRM_LEAD_TASKS_DEFAULT_PER_PAGE = 20

/** @deprecated используйте CRM_LEAD_TASKS_DEFAULT_PER_PAGE */
export const CRM_LEAD_TASKS_PAGE_LIMIT = CRM_LEAD_TASKS_DEFAULT_PER_PAGE

export const CRM_LEAD_TASKS_STATUS_OPTIONS: Array<{
  label: string
  value: CrmLeadTasksStatus
}> = [
  { label: 'Все', value: 'all' },
  { label: 'Просроченные', value: 'overdue' },
  { label: 'Выполненные', value: 'completed' },
]

export function parseCrmLeadTasksStatus(
  raw: unknown,
): CrmLeadTasksStatus {
  if (
    raw === 'all' ||
    raw === 'current' ||
    raw === 'overdue' ||
    raw === 'completed'
  ) {
    return raw
  }
  return 'all'
}

/** Роли, которым для create нужен `manager_id` = `user.id`. */
export function leadTaskCreateRequiresManagerId(
  role: string | null | undefined,
): boolean {
  return (
    role === 'admin' ||
    role === 'junior-admin' ||
    role === 'head-of-sales'
  )
}

export function canCreateLeadTask(role: string | null | undefined): boolean {
  return (
    role === 'admin' ||
    role === 'junior-admin' ||
    role === 'head-of-sales' ||
    role === 'sales-manager'
  )
}

/**
 * `crm_managers.id` → `user.id` для create-task.
 * Нельзя передавать card `manager.id` как `manager_id`.
 */
export function resolveManagerUserId(
  managers: Manager[],
  crmManagerId: number | null | undefined,
): number | null {
  if (crmManagerId == null || !Number.isFinite(crmManagerId) || crmManagerId <= 0) {
    return null
  }
  const found = managers.find((m) => m.id === crmManagerId)
  const userId = found?.user?.id
  if (userId == null || !Number.isFinite(userId) || userId <= 0) return null
  return userId
}

export function formatLeadTaskDeadline(
  deadline: string | null | undefined,
): string | null {
  if (deadline == null || String(deadline).trim() === '') return null
  const d = dayjs(String(deadline).trim())
  return d.isValid() ? d.format('DD.MM.YYYY') : String(deadline)
}

export function isLeadTaskOverdue(
  task: Pick<CrmLeadTask, 'deadline' | 'is_completed'>,
): boolean {
  if (task.is_completed) return false
  if (task.deadline == null || String(task.deadline).trim() === '') return false
  const d = dayjs(String(task.deadline).trim()).startOf('day')
  if (!d.isValid()) return false
  return d.isBefore(dayjs().startOf('day'))
}

export function getLeadTaskPerformerLabel(task: CrmLeadTask): string | null {
  const fromPerformers = task.performers
    ?.map((p) => p.user?.name?.trim())
    .filter((name): name is string => Boolean(name))
  if (fromPerformers && fromPerformers.length > 0) {
    return fromPerformers.join(', ')
  }
  return null
}

const getLeadTaskPerformerIds = (task: CrmLeadTask): number[] => {
  const fromPerformers = normalizeTaskPerformerIds(
    (task.performers ?? [])
      .map((p) => p.user_id ?? p.user?.id)
      .filter((id): id is number => id != null),
  )
  if (fromPerformers.length > 0) return fromPerformers

  const performerSticker = (task.stickers ?? []).find(
    (s) => s.field_type === 'performer' || s.system_key === 'performer',
  )
  const stickerValue = performerSticker?.value
  if (!Array.isArray(stickerValue)) return []

  return normalizeTaskPerformerIds(
    stickerValue
      .map((item) => {
        if (item != null && typeof item === 'object' && 'id' in item) {
          return Number((item as { id: unknown }).id)
        }
        return Number(item)
      })
      .filter((id) => Number.isFinite(id) && id > 0),
  )
}

const toLeadTaskFieldValue = (
  sticker: CrmLeadTaskSticker,
): CreateTaskTrackerTaskFieldValue | null => {
  const fieldSettingId = sticker.field_setting_id
  if (fieldSettingId == null || !Number.isFinite(fieldSettingId)) return null

  const fieldType = String(sticker.field_type ?? '').trim()
  const systemKey = sticker.system_key
  const raw = sticker.value

  if (systemKey === CRM_LEAD_TASKS_SYSTEM_KEY || fieldType === 'external_reference') {
    if (raw != null && typeof raw === 'object' && !Array.isArray(raw)) {
      const leadFunnelId = (raw as { lead_funnel_id?: unknown }).lead_funnel_id
      if (leadFunnelId != null && String(leadFunnelId).trim() !== '') {
        return {
          field_setting_id: fieldSettingId,
          value: String(leadFunnelId).trim(),
        }
      }
    }
    if (typeof raw === 'string' || typeof raw === 'number') {
      return { field_setting_id: fieldSettingId, value: raw }
    }
    return null
  }

  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return { field_setting_id: fieldSettingId, value: raw }
  }

  if (typeof raw === 'string') {
    const trimmed = raw.trim()
    if (!trimmed) return null
    if (fieldType === 'number') {
      const numericValue = Number(trimmed)
      if (!Number.isFinite(numericValue)) return null
      return { field_setting_id: fieldSettingId, value: numericValue }
    }
    return { field_setting_id: fieldSettingId, value: trimmed }
  }

  return null
}

/**
 * Payload для update при смене `is_completed`.
 * Бэк валидирует обязательные стикеры по телу запроса, а не по уже
 * сохранённым значениям — поэтому прокидываем текущие значения.
 */
export function buildLeadTaskStatusUpdatePayload(
  task: CrmLeadTask,
  isCompleted: boolean,
): UpdateTaskTrackerTaskRequest {
  const payload: UpdateTaskTrackerTaskRequest = {
    is_completed: isCompleted,
  }

  const requiredStickers = (task.stickers ?? []).filter(
    (sticker) => sticker.is_required === true,
  )

  const fieldValues: CreateTaskTrackerTaskFieldValue[] = []

  for (const sticker of requiredStickers) {
    const fieldType = String(sticker.field_type ?? '').trim()
    const systemKey = sticker.system_key

    if (isTaskTrackerSystemDatetimeStickerFieldType(fieldType, systemKey)) {
      continue
    }

    if (fieldType === 'deadline' || systemKey === 'deadline') {
      const deadline =
        normalizeTaskDeadline(task.deadline) ??
        normalizeTaskDeadline(
          typeof sticker.value === 'string' ? sticker.value : null,
        )
      if (deadline) payload.deadline = deadline
      continue
    }

    if (fieldType === 'performer' || systemKey === 'performer') {
      const performerIds = getLeadTaskPerformerIds(task)
      if (performerIds.length > 0) payload.performer_ids = performerIds
      continue
    }

    const fieldValue = toLeadTaskFieldValue(sticker)
    if (fieldValue) fieldValues.push(fieldValue)
  }

  // Fallback: дедлайн на задаче есть, а в stickers флаг мог не прийти.
  if (payload.deadline == null) {
    const deadline = normalizeTaskDeadline(task.deadline)
    if (deadline) payload.deadline = deadline
  }

  if (fieldValues.length > 0) {
    payload.field_values = fieldValues
  }

  return payload
}
